// src/components/dashboard/ComplaintCard.js
// FIXED: Shows latest conversation message instead of original complaint content
// ADDED: Ticket ID display
// UPDATED: Real-time message updates via WebSocket

import React, { useState, useEffect } from 'react';
import { Clock, AlertTriangle, CheckCircle, User, Mail, Facebook, Instagram, MessageCircle, Linkedin, Phone, Hash } from 'lucide-react';
import { PRIORITY_COLORS, STATUS_COLORS, SOURCE_CONFIG } from '../../utils/constants';
import webSocketService from '../../services/websocket';

const ComplaintCard = ({ complaint, onClick, onStatusUpdate }) => {
  // State to track real-time updates
  const [localComplaint, setLocalComplaint] = useState(complaint);
  const [messageCount, setMessageCount] = useState(
    complaint.conversation?.messages?.length || 0
  );

  // Update local state when prop changes
  useEffect(() => {
    setLocalComplaint(complaint);
    setMessageCount(complaint.conversation?.messages?.length || 0);
  }, [complaint]);

  // Listen for real-time message updates
  useEffect(() => {
    if (!complaint?.ticketId) return;

    const handleNewMessage = (data) => {
      console.log('📨 ComplaintCard received new message:', data);
      
      // Check if this message is for this complaint
      if (data.ticketId === complaint.ticketId) {
        console.log('✅ Message is for this ticket, updating card');
        
        // Increment message count
        setMessageCount(prev => prev + 1);
        
        // Update the latest message content
        if (data.message || data.content) {
          setLocalComplaint(prev => ({
            ...prev,
            conversation: {
              ...prev.conversation,
              lastMessageAt: data.sentAt || data.timestamp || new Date(),
              messages: prev.conversation?.messages
                ? [
                    {
                      id: data.messageId || data.conversationId,
                      content: data.content || data.message,
                      messageType: data.messageType || 'OUTBOUND',
                      fromEmail: data.fromEmail,
                      createdAt: data.createdAt || data.timestamp,
                      sentAt: data.sentAt || data.timestamp,
                      hasAttachments: data.hasAttachments || false,
                      attachments: data.attachments || null
                    },
                    ...prev.conversation.messages
                  ]
                : [{
                    id: data.messageId || data.conversationId,
                    content: data.content || data.message,
                    messageType: data.messageType || 'OUTBOUND',
                    fromEmail: data.fromEmail,
                    createdAt: data.createdAt || data.timestamp,
                    sentAt: data.sentAt || data.timestamp,
                    hasAttachments: data.hasAttachments || false,
                    attachments: data.attachments || null
                  }]
            }
          }));
        }
      }
    };

    // Add listener for new messages
    webSocketService.on('new_message', handleNewMessage);

    // Cleanup
    return () => {
      webSocketService.off('new_message', handleNewMessage);
    };
  }, [complaint?.ticketId]);

  const handleDragStart = (e) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('complaintId', localComplaint.id);
    e.dataTransfer.setData('currentStatus', localComplaint.status);
    e.currentTarget.classList.add('opacity-50', 'ring-2', 'ring-purple-400', 'shadow-xl');
  };

  const handleDragEnd = (e) => {
    e.currentTarget.classList.remove('opacity-50', 'ring-2', 'ring-purple-400', 'shadow-xl');
  };

  const getPriorityIcon = () => {
    switch(localComplaint.priority?.toUpperCase()) {
      case 'URGENT':
        return <AlertTriangle className="w-4 h-4 text-red-600" />;
      case 'HIGH':
        return <AlertTriangle className="w-4 h-4 text-orange-600" />;
      case 'MEDIUM':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      default:
        return <CheckCircle className="w-4 h-4 text-green-600" />;
    }
  };

  const getCustomerName = () => {
    if (localComplaint.customer && typeof localComplaint.customer === 'object') {
      return localComplaint.customer.name;
    }
    return localComplaint.customerName || localComplaint.customer_name || 'Unknown Customer';
  };

  const getCustomerEmail = () => {
    if (localComplaint.customer && typeof localComplaint.customer === 'object') {
      return localComplaint.customer.email;
    }
    return localComplaint.customerEmail || localComplaint.customer_email || '';
  };

  const getAssigneeName = () => {
    if (localComplaint.assignedAgent && typeof localComplaint.assignedAgent === 'object') {
      return localComplaint.assignedAgent.name || localComplaint.assignedAgent.email || 'Unassigned';
    }
    
    if (localComplaint.assignedTo && typeof localComplaint.assignedTo === 'string') {
      const isId = localComplaint.assignedTo.startsWith('cm') || 
                   localComplaint.assignedTo.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
      
      if (!isId) {
        return localComplaint.assignedTo;
      }
      
      return 'Assigned';
    }
    
    return 'Unassigned';
  };

  const getTimeAgo = () => {
    // Use lastMessageAt from conversation if available, otherwise use complaint createdAt
    const timestamp = localComplaint.conversation?.lastMessageAt || localComplaint.updatedAt || localComplaint.createdAt || localComplaint.created_at;
    
    const created = new Date(timestamp);
    const now = new Date();
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    if (diffMins > 0) return `${diffMins}m ago`;
    return 'Just now';
  };

  const getSourceBadge = () => {
    const source = localComplaint.source || 'EMAIL';
    const config = SOURCE_CONFIG[source];
    
    if (!config) return null;

    const IconMap = {
      Mail,
      Facebook,
      Instagram,
      MessageCircle,
      Linkedin,
      Phone
    };

    const IconComponent = IconMap[config.icon];

    return (
      <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${config.color}`}>
        {IconComponent && <IconComponent className={`w-3 h-3 ${config.iconColor}`} />}
        <span>{config.label}</span>
      </div>
    );
  };

  // FIXED: Get the latest message content from conversation
  const getLatestMessageContent = () => {
    // If conversation exists and has messages, show the latest one
    if (localComplaint.conversation && localComplaint.conversation.messages && localComplaint.conversation.messages.length > 0) {
      // Messages should be sorted by createdAt desc, so first one is latest
      const latestMessage = localComplaint.conversation.messages[0];
      return latestMessage.content || localComplaint.content || localComplaint.description || 'No description available';
    }
    
    // Fallback to original complaint content
    return localComplaint.content || localComplaint.description || 'No description available';
  };

  // Get message count for display
  const getMessageCount = () => {
    return messageCount;
  };

  // Get ticket ID
  const getTicketId = () => {
    return localComplaint.ticketId || 'N/A';
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={() => onClick(localComplaint)}
      className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 cursor-pointer hover:shadow-md transition-all hover:border-purple-200 space-y-3"
    >
      {/* Header with Ticket ID, Priority, Source, and Time */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Ticket ID Badge */}
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-mono font-semibold">
            <Hash className="w-3 h-3" />
            {getTicketId()}
          </span>
          {getPriorityIcon()}
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            PRIORITY_COLORS[localComplaint.priority?.toUpperCase()] || 'bg-gray-100 text-gray-600'
          }`}>
            {localComplaint.priority || 'MEDIUM'}
          </span>
          {getSourceBadge()}
        </div>
        <div className="flex flex-col items-end">
          <span className="text-xs text-gray-500">{getTimeAgo()}</span>
          {getMessageCount() > 1 && (
            <span className="text-xs text-purple-600 font-medium mt-1">
              {getMessageCount()} messages
            </span>
          )}
        </div>
      </div>

      {/* Subject */}
      <h4 className="font-medium text-gray-900 line-clamp-1">
        {localComplaint.subject || 'No subject'}
      </h4>

      {/* Content Preview - FIXED: Shows latest message */}
      <p className="text-sm text-gray-600 line-clamp-2">
        {getLatestMessageContent()}
      </p>

      {/* Customer Info */}
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <User className="w-3 h-3" />
        <span className="truncate">{getCustomerName()}</span>
        {getCustomerEmail() && !getCustomerEmail().includes('facebook_') && (
          <>
            <Mail className="w-3 h-3 ml-1" />
            <span className="truncate">{getCustomerEmail()}</span>
          </>
        )}
      </div>

      {/* Footer with Category and Assignment */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        <span className="text-xs text-gray-500 capitalize">
          {localComplaint.category?.toLowerCase() || 'general'}
        </span>
        
        <div className="flex items-center gap-1">
          <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
            <span className="text-[10px] text-purple-600 font-medium">
              {getAssigneeName().charAt(0).toUpperCase()}
            </span>
          </div>
          <span className="text-xs text-gray-600 truncate max-w-[100px]">
            {getAssigneeName()}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ComplaintCard;